import {
    Badge,
    Button,
    Card,
    CardHeader,
    PageHeader,
    TextField,
    Toggle,
    WriteError,
} from '@shared/ui';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { useClientTarget } from '../hooks/use-client-target';
import {
    useAddressAvailability,
    useCreateClient,
    useUsernameAvailability,
} from '../hooks/use-clients';
import { describeTarget, targetParams } from '../schemas/target';

import { EnvironmentScope } from './EnvironmentScope';
import {
    classifyFailure,
    EMPTY_DRAFT,
    emailError,
    isStepOneComplete,
    isStepTwoComplete,
    slugError,
    slugify,
    stepForFailure,
    usernameError,
    type CreateFailure,
    type Draft,
} from './wizard-state';

import type { CreatedClient } from '../schemas/client';

/**
 * Create a client and its first person.
 *
 * ## Why a route and not a dialog
 *
 * The flags screens create in a `Modal`, and for a seven-field form that is
 * right. This flow ends by showing a password that cannot be recovered: Escape
 * or a click on the backdrop would lose it, and `dismissable={false}` only
 * covers the write, not the screen after it.
 *
 * ## Why the review step exists
 *
 * The same reason `CreateFlagDialog` has one: a typo'd address is not an error,
 * it is a second client — permanent, un-renameable, and looking exactly as
 * legitimate as the one that was meant. The step puts the address alone in
 * front of the operator after they have stopped typing.
 */
export function NewClientWizard() {
    const targetState = useClientTarget();
    const { target } = targetState;
    const navigate = useNavigate();

    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
    const [failure, setFailure] = useState<CreateFailure | null>(null);
    const [created, setCreated] = useState<CreatedClient | null>(null);

    const address = useAddressAvailability(target, draft.slug);
    const handle = useUsernameAvailability(target, draft.username.trim());
    const create = useCreateClient(target);

    const set = <K extends keyof Draft>(field: K, value: Draft[K]) => {
        setDraft((current) => ({ ...current, [field]: value }));
        setFailure(null);
    };

    if (created) return <CredentialsScreen created={created} target={target} />;
    if (failure?.kind === 'pending') {
        return <HalfCreatedScreen failure={failure} draft={draft} target={target} />;
    }

    const blocked = targetState.isSlotMissing || targetState.isSlotDirty;

    /** The address check runs HERE, on the step that owns the field. */
    const continueFromStepOne = async () => {
        const result = await address.refetch();
        if (result.data?.available === false) {
            setFailure({
                kind: 'address',
                message: result.data.heldBy
                    ? `Taken in ${describeTarget(target)} by “${result.data.heldBy.displayName}”.`
                    : `Taken in ${describeTarget(target)}.`,
            });
            return;
        }
        setFailure(null);
        setStep(2);
    };

    /**
     * The handle check runs on the step that owns the field, like the address.
     *
     * It is a snapshot and cannot be a guarantee — somebody can take the name
     * between this answer and the create — which is why `OWNER_CONFLICT` is
     * still handled on submit and still routes back here. What this buys is
     * that the common case costs one step instead of three.
     */
    const continueFromStepTwo = async () => {
        const result = await handle.refetch();
        if (result.data?.available === false) {
            setFailure({
                kind: 'owner',
                message: 'That username is already taken. Choose a different one.',
                field: 'username',
            });
            return;
        }
        setFailure(null);
        setStep(3);
    };

    const submit = () => {
        create.mutate(
            {
                name: draft.name.trim(),
                slug: draft.slug.trim(),
                orgId: draft.orgId.trim() || null,
                isSandbox: draft.isSandbox,
                owner: { username: draft.username.trim(), email: draft.email.trim() },
            },
            {
                onSuccess: setCreated,
                onError: (error) => {
                    const classified = classifyFailure(error);
                    setFailure(classified);
                    setStep(stepForFailure(classified));
                },
            },
        );
    };

    return (
        <>
            <PageHeader
                eyebrow="Tenants · Onboarding"
                title="New client"
                description="Create the client and its first administrator. You will receive their credentials once, at the end."
                actions={
                    <Button variant="ghost" onClick={() => void navigate(-1)}>
                        Cancel
                    </Button>
                }
            />

            <EnvironmentScope state={targetState} />
            <Stepper step={step} />

            {step === 1 && (
                <Card>
                    <CardHeader title="The client" subtitle="Step 1 of 3" />
                    <div className="flex flex-col gap-5 p-5">
                        {failure?.kind === 'address' && (
                            <WriteError error={new Error(failure.message)} />
                        )}

                        <TextField
                            label="Client name"
                            value={draft.name}
                            onChange={(name) => {
                                setDraft((current) => ({
                                    ...current,
                                    name,
                                    slug: current.slugTouched ? current.slug : slugify(name),
                                }));
                                setFailure(null);
                            }}
                            placeholder="Fundación Botero"
                            hint="As it appears in the application. Can be changed later."
                            autoFocus
                        />

                        <TextField
                            label="Public address — permanent"
                            value={draft.slug}
                            onChange={(slug) => {
                                setDraft((current) => ({ ...current, slug, slugTouched: true }));
                                setFailure(null);
                            }}
                            placeholder="fundacion-botero"
                            mono
                            error={slugError(draft.slug)}
                            hint={
                                <>
                                    This is the address the client’s visitors use to buy tickets and
                                    memberships. It <b>cannot be changed or reused</b> once the
                                    client exists.
                                </>
                            }
                        />

                        <TextField
                            label="Salesforce Org ID — optional"
                            value={draft.orgId}
                            onChange={(orgId) => {
                                set('orgId', orgId);
                            }}
                            placeholder="00D5f000004XyZaEAK"
                            mono
                            hint="Only if the client also exists in Salesforce. It can be connected later."
                        />

                        {/* `Toggle` is the switch alone — the label and the
                            reason live beside it, as they do on the flags
                            screens. */}
                        <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-3">
                            <Toggle
                                on={draft.isSandbox}
                                onChange={(isSandbox) => {
                                    set('isSandbox', isSandbox);
                                }}
                                aria-label="Sandbox client"
                            />
                            <div className="text-[13px]">
                                <b className="font-semibold">Sandbox client</b>
                                <p className="mt-0.5 text-[12px] text-gray-500">
                                    Disposable data. Excluded from reporting and billing.
                                </p>
                            </div>
                        </div>
                    </div>
                    <Footer>
                        <Button
                            disabled={!isStepOneComplete(draft) || address.isFetching || blocked}
                            onClick={() => void continueFromStepOne()}
                        >
                            {address.isFetching ? 'Checking…' : 'Continue'}
                        </Button>
                    </Footer>
                </Card>
            )}

            {step === 2 && (
                <Card>
                    <CardHeader title="The owner" subtitle="Step 2 of 3" />
                    <div className="flex flex-col gap-5 p-5">
                        {failure?.kind === 'owner' && (
                            <WriteError error={new Error(failure.message)} />
                        )}

                        <Notice tone="info">
                            The client’s first person, and the only one who can sign in at the
                            start. From inside, they add the rest of their team.
                        </Notice>

                        <TextField
                            label="Username"
                            value={draft.username}
                            onChange={(username) => {
                                set('username', username);
                            }}
                            placeholder="a.gomez"
                            mono
                            autoFocus
                            error={
                                failure?.kind === 'owner' && failure.field === 'username'
                                    ? failure.message
                                    : usernameError(draft.username)
                            }
                            hint="What they sign in with. The client chooses it — we do not generate it. Handles are unique across every client."
                        />

                        <TextField
                            label="Email"
                            value={draft.email}
                            onChange={(email) => {
                                set('email', email);
                            }}
                            placeholder="admin@botero.org"
                            error={
                                failure?.kind === 'owner' && failure.field === 'email'
                                    ? failure.message
                                    : emailError(draft.email)
                            }
                            hint="Contact only. Not what they sign in with."
                        />

                        <Notice tone="warn">
                            <b>No email is sent.</b> The password is shown on the next screen, once,
                            and you deliver it through whatever channel you use with this client.
                        </Notice>
                    </div>
                    <Footer>
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setStep(1);
                            }}
                        >
                            Back
                        </Button>
                        <span className="ml-auto" />
                        <Button
                            disabled={!isStepTwoComplete(draft) || handle.isFetching || blocked}
                            onClick={() => void continueFromStepTwo()}
                        >
                            {handle.isFetching ? 'Checking…' : 'Continue'}
                        </Button>
                    </Footer>
                </Card>
            )}

            {step === 3 && (
                <Card>
                    <CardHeader title="Review" subtitle="Step 3 of 3" />
                    <div className="flex flex-col gap-5 p-5">
                        {failure?.kind === 'unknown' && (
                            <WriteError error={new Error(failure.message)} />
                        )}

                        <div>
                            <p className="mb-2 text-[12.5px] text-gray-500">
                                The public address is fixed from this moment on:
                            </p>
                            <span className="inline-flex items-center rounded-lg border border-dashed border-brand-200 bg-brand-50 px-3 py-1.5 font-mono text-[13.5px] font-medium text-brand-700">
                                {draft.slug}
                            </span>
                        </div>

                        <dl className="grid grid-cols-[190px_1fr] text-[13.5px]">
                            <Row label="Client">{draft.name}</Row>
                            <Row label="Environment">{describeTarget(target)}</Row>
                            <Row label="Salesforce Org">
                                {draft.orgId || (
                                    <span className="text-gray-400">Not connected</span>
                                )}
                            </Row>
                            <Row label="Type">
                                {draft.isSandbox ? 'Sandbox client' : 'Live client'}
                            </Row>
                            <Row label="Owner username">
                                <span className="font-mono">{draft.username}</span>
                            </Row>
                            <Row label="Contact email">{draft.email}</Row>
                        </dl>

                        <Notice tone="warn">
                            On creation the password is shown <b>once</b>. It cannot be recovered or
                            resent. Have somewhere to put it.
                        </Notice>
                    </div>
                    <Footer>
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setStep(2);
                            }}
                        >
                            Back
                        </Button>
                        <span className="ml-auto" />
                        <Button disabled={create.isPending || blocked} onClick={submit}>
                            {create.isPending ? 'Creating…' : `Create in ${describeTarget(target)}`}
                        </Button>
                    </Footer>
                </Card>
            )}
        </>
    );
}

/* ------------------------------------------------------------- terminals - */

/**
 * The password, shown once and never stored.
 *
 * An empty one is a real outcome rather than a missing field: a request that
 * completed a previously half-finished provisioning has no fresh password —
 * it was handed out on the attempt that created the credential. Saying so
 * beats rendering a blank.
 */
function CredentialsScreen({
    created,
    target,
}: {
    created: CreatedClient;
    target: { env: string };
}) {
    const [acknowledged, setAcknowledged] = useState(false);
    const search = new URLSearchParams(targetParams(target as never)).toString();

    return (
        <>
            <PageHeader
                eyebrow="Tenants · Onboarding"
                title="Client created"
                description={`${created.name} exists and its owner can sign in. Hand them these credentials.`}
            />

            <Card>
                <CardHeader
                    title="Sign-in credentials"
                    action={
                        <Badge tone="warning" dot>
                            Shown once
                        </Badge>
                    }
                />
                <div className="flex flex-col gap-3 p-5">
                    <Credential label="Username" value={created.owner.username} />

                    {created.owner.temporaryPassword ? (
                        <Credential
                            label="Temporary password"
                            value={created.owner.temporaryPassword}
                            secret
                        />
                    ) : (
                        <Notice tone="info">
                            This attempt carried no new password — it was handed out on the one that
                            created the credential. If it was lost, the only way out today is to
                            create another person.
                        </Notice>
                    )}

                    <Notice tone="warn">
                        Leaving this screen removes the password from the Backoffice. There is no
                        recovery and no resend.
                    </Notice>
                </div>
                <Footer>
                    <label className="flex cursor-pointer items-start gap-2.5 text-[13px]">
                        <input
                            type="checkbox"
                            checked={acknowledged}
                            onChange={(event) => {
                                setAcknowledged(event.target.checked);
                            }}
                            className="mt-0.5 size-4 accent-brand"
                        />
                        <span>I saved the password and delivered it to the client</span>
                    </label>
                    <span className="ml-auto" />
                    <Link to={{ pathname: '/clients', search }} tabIndex={acknowledged ? 0 : -1}>
                        <Button disabled={!acknowledged}>Done</Button>
                    </Link>
                </Footer>
            </Card>
        </>
    );
}

/**
 * The client exists and its person does not.
 *
 * The only failure where retrying is the right offer: both halves of the owner
 * call are safe to repeat upstream. What must NOT be offered is submitting the
 * whole thing again — that would now fail on the address this client holds.
 */
function HalfCreatedScreen({
    failure,
    draft,
    target,
}: {
    failure: CreateFailure;
    draft: Draft;
    target: { env: string };
}) {
    const search = new URLSearchParams(targetParams(target as never)).toString();

    return (
        <>
            <PageHeader
                eyebrow="Tenants · Onboarding"
                title="Half-created"
                description="The client was created, but its owner was not. They cannot sign in yet."
            />
            <Card>
                <CardHeader title="Missing owner" subtitle="The client exists and is listed" />
                <div className="flex flex-col gap-4 p-5">
                    {/* NOT `WriteError`. Its heading is "That change did not
                        save", which is right for every other failure and wrong
                        for this one: half of it saved, and the half that did is
                        the whole reason this screen exists. Two sentences that
                        contradict each other leave the operator unsure whether
                        the client is there — the one thing they need to know. */}
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3">
                        <p className="text-[13px] font-semibold text-amber-900">
                            The client was created. Its owner was not.
                        </p>
                        <p className="mt-1 text-[12.5px] leading-relaxed text-amber-800">
                            {failure.message}
                        </p>
                    </div>
                    <dl className="grid grid-cols-[190px_1fr] text-[13.5px]">
                        <Row label="Client">{draft.name}</Row>
                        <Row label="Address">
                            <span className="font-mono">{draft.slug}</span>
                        </Row>
                        <Row label="Username">
                            <span className="font-mono">{draft.username}</span>
                        </Row>
                        <Row label="State">Created, no owner</Row>
                    </dl>
                    <Notice tone="info">
                        <b>Do not create it again.</b> The address is taken by this client, so a
                        second attempt would be refused. Finishing it needs a route that provisions
                        an owner into an existing client — until that exists, the client is listed
                        and nobody can sign into it.
                    </Notice>
                </div>
                <Footer>
                    <span className="ml-auto" />
                    <Link to={{ pathname: '/clients', search }}>
                        <Button variant="ghost">Back to clients</Button>
                    </Link>
                </Footer>
            </Card>
        </>
    );
}

/* --------------------------------------------------------------- pieces - */

function Stepper({ step }: { step: 1 | 2 | 3 }) {
    const labels = ['Client', 'Owner', 'Review'];

    return (
        <div className="mb-5 flex flex-wrap items-center">
            {labels.map((label, index) => {
                const number = index + 1;
                const state = number === step ? 'current' : number < step ? 'done' : 'todo';

                return (
                    <div key={label} className="flex items-center">
                        {index > 0 && <span className="mx-3 h-px w-8 bg-gray-200" />}
                        <span
                            className={
                                state === 'current'
                                    ? 'flex size-6 items-center justify-center rounded-full bg-brand text-[11.5px] font-semibold text-white'
                                    : state === 'done'
                                      ? 'flex size-6 items-center justify-center rounded-full border border-brand-200 bg-brand-50 text-[11.5px] font-semibold text-brand-700'
                                      : 'flex size-6 items-center justify-center rounded-full border border-gray-200 bg-white text-[11.5px] font-semibold text-gray-400'
                            }
                        >
                            {number}
                        </span>
                        <span
                            className={`ml-2.5 text-[12.5px] ${state === 'todo' ? 'text-gray-400' : 'font-semibold text-gray-900'}`}
                        >
                            {label}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

function Footer({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex items-center gap-2.5 border-t border-gray-100 bg-gray-50 px-5 py-3.5">
            {children}
        </div>
    );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <>
            <dt className="border-b border-gray-100 py-2.5 text-[12.5px] text-gray-500">{label}</dt>
            <dd className="m-0 break-words border-b border-gray-100 py-2.5">{children}</dd>
        </>
    );
}

function Notice({ tone, children }: { tone: 'info' | 'warn'; children: React.ReactNode }) {
    return (
        <div
            className={
                tone === 'warn'
                    ? 'rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-[12.5px] leading-relaxed text-amber-800'
                    : 'rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-3 text-[12.5px] leading-relaxed text-blue-800'
            }
        >
            {children}
        </div>
    );
}

function Credential({ label, value, secret }: { label: string; value: string; secret?: boolean }) {
    const [copied, setCopied] = useState(false);

    return (
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-3">
            <span className="w-36 shrink-0 text-[12px] text-gray-500">{label}</span>
            <span
                className={`flex-1 break-all font-mono text-[13.5px] ${secret ? 'font-medium' : ''}`}
            >
                {value}
            </span>
            <button
                type="button"
                onClick={() => {
                    void navigator.clipboard.writeText(value).finally(() => {
                        setCopied(true);
                        setTimeout(() => {
                            setCopied(false);
                        }, 1600);
                    });
                }}
                className="shrink-0 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[12px] font-semibold text-gray-700 hover:bg-gray-50"
            >
                {copied ? 'Copied' : 'Copy'}
            </button>
        </div>
    );
}
